if (!customElements.get("cart-drawer")) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
  
      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.addEventListener('click', (event) => {
        if (event.target.id === 'CartDrawer-Overlay') this.close();
      });
      // Use delegation so arrows still work after cart sections are replaced.
      this.addEventListener('click', (event) => {
        const button = event.target.closest('.cross_sells .button-slide');
        if (!button) return;
        const scroller = button.closest('.cross_sells').querySelector('.product_cross_sells');
        if (!scroller) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const slide = scroller.querySelector('.slider__slide');
        const step = slide?.getBoundingClientRect().width || scroller.clientWidth;
        const direction = button.classList.contains('button-slide_prev') ? -1 : 1;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        scroller.scrollBy({ left: direction * step, behavior: reduceMotion ? 'auto' : 'smooth' });
      }, true);
      this.addEventListener('submit', (event) => {
        if (!event.target.matches('[data-cross-sell-form]')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.addCrossSellItem(event.target);
      }, true);
      this.setHeaderCartIconAccessibility();
    }
  
    crossSellForm(productId) {
      return [...this.querySelectorAll('[data-cross-sell-form]')].find((form) => form.dataset.crossSellProduct === productId);
    }

    setCrossSellBusy() {
      this.querySelectorAll('[data-cross-sell-form]').forEach((form) => {
        const button = form.querySelector('[data-cross-sell-add]');
        if (button) {
          button.disabled = Boolean(this.crossSellAdding) || button.dataset.available !== 'true';
          button.setAttribute('aria-disabled', String(button.disabled));
          button.textContent = this.crossSellAdding && form.dataset.crossSellProduct === this.crossSellProductId
            ? 'Adding…' : button.dataset.label;
        }
        form.setAttribute('aria-busy', String(Boolean(this.crossSellAdding)));
        const select = form.querySelector('[data-cross-sell-variant]');
        if (select) select.disabled = Boolean(this.crossSellAdding) || select.dataset.available !== 'true';
      });
    }

    crossSellMessage(productId, text, error = false) {
      const message = this.crossSellForm(productId)?.querySelector('[data-cross-sell-message]');
      if (!message) return;
      message.textContent = text;
      message.hidden = !text;
      message.setAttribute('role', error ? 'alert' : 'status');
    }

    renderCrossSellCart(sections) {
      if (!sections?.['cart-drawer'] || !sections['cart-icon-bubble']) return false;
      const drawerDocument = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const sourceDrawer = drawerDocument.querySelector('cart-drawer');
      const sourceContent = sourceDrawer?.querySelector('#CartDrawer');
      const targetContent = this.querySelector('#CartDrawer');
      const sourceBubble = this.getSectionDOM(sections['cart-icon-bubble']);
      const targetBubble = document.getElementById('cart-icon-bubble');
      if (!sourceContent?.querySelector('cart-drawer-items') || !targetContent || !sourceBubble || !targetBubble) return false;
      const content = this.querySelector('.drawer__content');
      const bottomGap = content ? content.scrollHeight - content.clientHeight - content.scrollTop : 0;
      const horizontalScroll = this.querySelector('.product_cross_sells')?.scrollLeft || 0;
      const selections = new Map([...this.querySelectorAll('[data-cross-sell-form]')].map((form) => [form.dataset.crossSellProduct, form.querySelector('[name="id"]')?.value]));
      const hadFocus = this.contains(document.activeElement);
      this.cartRefreshRevision = (this.cartRefreshRevision || 0) + 1;
      targetContent.innerHTML = sourceContent.innerHTML;
      targetBubble.innerHTML = sourceBubble.innerHTML;
      this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
      this.querySelectorAll('[data-cross-sell-form]').forEach((form) => {
        const select = form.querySelector('[data-cross-sell-variant]');
        const previous = selections.get(form.dataset.crossSellProduct);
        if (select && [...select.options].some((option) => option.value === previous && !option.disabled)) select.value = previous;
      });
      const nextContent = this.querySelector('.drawer__content');
      if (nextContent) nextContent.scrollTop = nextContent.scrollHeight - nextContent.clientHeight - bottomGap;
      const carousel = this.querySelector('.product_cross_sells');
      if (carousel) carousel.scrollLeft = horizontalScroll;
      this.setCrossSellBusy();
      if (hadFocus && this.classList.contains('active')) {
        const inner = this.querySelector('.drawer__inner');
        trapFocus(inner, inner);
      }
      if (typeof updateFreeShippingMessage === 'function') updateFreeShippingMessage();
      return true;
    }

    async addCrossSellItem(form) {
      const button = form.querySelector('[data-cross-sell-add]');
      const variantId = form.querySelector('[name="id"]')?.value;
      if (this.crossSellAdding || !button || button.disabled || !variantId) return;
      const productId = form.dataset.crossSellProduct;
      this.crossSellAdding = true;
      this.crossSellProductId = productId;
      this.cartRefreshRevision = (this.cartRefreshRevision || 0) + 1;
      this.crossSellMessage(productId, '');
      this.setCrossSellBusy();
      let added = false;
      let failureMessage = 'We could not confirm this item was added. Please reopen your cart before trying again.';
      try {
        const url = new URL(routes.cart_add_url || (window.Shopify?.routes?.root || '/') + 'cart/add', window.location.origin);
        if (!url.pathname.endsWith('.js')) url.pathname += '.js';
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          failureMessage = typeof data.description === 'string' && data.description.trim()
            ? data.description : 'This item could not be added. Please try again.';
          throw new Error(failureMessage);
        }
        added = true;
        if (!this.renderCrossSellCart(data.sections)) {
          const refreshURL = new URL(window.location.pathname, window.location.origin);
          refreshURL.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
          const refresh = await fetch(refreshURL.toString());
          if (!refresh.ok || !this.renderCrossSellCart(await refresh.json())) throw new Error('The cart could not refresh.');
        }
        this.crossSellMessage(productId, 'Added to your cart.');
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', productVariantId: variantId, cartData: data });
        }
      } catch (error) {
        this.crossSellMessage(productId, added
          ? 'This item was added, but the cart could not refresh. Please reopen your cart.'
          : failureMessage, true);
      } finally {
        this.crossSellAdding = false;
        this.setCrossSellBusy();
        if (this.classList.contains('active')) {
          this.crossSellForm(productId)?.querySelector('[data-cross-sell-add]:not(:disabled)')?.focus({ preventScroll: true });
        }
      }
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      if (!document.body.classList.contains('template-cart')) {
        cartLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(cartLink);
        });
        cartLink.addEventListener('keydown', (event) => {
          if (event.code.toUpperCase() === 'SPACE') {
            event.preventDefault();
            this.open(cartLink);
          }
        });
      }
    }
  
    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote) this.setSummaryAccessibility(cartDrawerNote);
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });
  
      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );
  
      document.documentElement.classList.add('overflow-hidden');
    }
  
    close() {
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.documentElement.classList.remove('overflow-hidden');
    }
  
    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');
  
      if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }
  
      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });
  
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  
    renderContents(parsedState) {
      this.cartRefreshRevision = (this.cartRefreshRevision || 0) + 1;
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
      const sourceDrawer = this.getSectionDOM(parsedState.sections['cart-drawer'], 'cart-drawer');
      if (sourceDrawer) this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
      setTimeout(() => {
        this.open();
        updateFreeShippingMessage();
      },10);
  
    }
  
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }
  
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
        },
      ];
    }
  
    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }
  
    setActiveElement(element) {
      this.activeElement = element;
    }
  }
  customElements.define("cart-drawer", CartDrawer);

  class CartDrawerItems extends CartItems {
    async onCartUpdate() {
      const drawer = this.closest('cart-drawer');
      if (!drawer) return;
      const revision = drawer.cartRefreshRevision = (drawer.cartRefreshRevision || 0) + 1;
      try {
        const url = new URL(routes.cart_url, window.location.origin);
        url.searchParams.set('section_id', 'cart-drawer');
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error('The cart drawer could not refresh.');
        const html = await response.text();
        if (!this.isConnected || revision !== drawer.cartRefreshRevision) return;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
        const sourceInner = source?.querySelector('.drawer__inner');
        const targetInner = drawer.querySelector('.drawer__inner');
        if (!sourceInner || !sourceInner.querySelector('cart-drawer-items') || !targetInner) {
          throw new Error('The cart drawer response was incomplete.');
        }
        const scrollTop = drawer.querySelector('.drawer__content')?.scrollTop || 0;
        const hadFocus = drawer.contains(document.activeElement);
        const focusedId = hadFocus ? document.activeElement.id : '';
        targetInner.innerHTML = sourceInner.innerHTML;
        drawer.classList.toggle('is-empty', source.classList.contains('is-empty'));
        const content = drawer.querySelector('.drawer__content');
        if (content) content.scrollTop = scrollTop;
        if (hadFocus && drawer.classList.contains('active')) {
          const previousFocus = focusedId ? document.getElementById(focusedId) : null;
          const focusTarget = previousFocus && drawer.contains(previousFocus) ? previousFocus : targetInner;
          trapFocus(targetInner, focusTarget);
        }
        if (typeof updateFreeShippingMessage === 'function') updateFreeShippingMessage();
      } catch (error) {
        console.error(error);
      }
    }

    updateQuantity(...args) {
      const drawer = this.closest('cart-drawer');
      if (drawer) drawer.cartRefreshRevision = (drawer.cartRefreshRevision || 0) + 1;
      return super.updateQuantity(...args);
    }

    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
        },
      ];
    }
  }
  if (!customElements.get("cart-drawer-items")) {
    customElements.define('cart-drawer-items', CartDrawerItems);
  }
}

